import { useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, MapPin, X, History, Trash2 } from 'lucide-react';
import { geoSearch, getRecent, addRecent, clearRecent } from '../lib/geocode';
import { kritisOn } from '../lib/policy';

const num = (s) => parseFloat(s.replace(',', '.'));

export function parseCoords(q) {
  const s = q.trim();
  const m = s.match(/^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/) || s.match(/^(-?\d+(?:[.,]\d+)?)[\s;]+(-?\d+(?:[.,]\d+)?)$/);
  if (!m) return null;
  const lat = num(m[1]); const lon = num(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null;
}

// picked/setPicked are owned by the parent so GPS can also fill them
export default function AddressSearch({ t, lang, online, onGo, picked, setPicked, children }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState(null);
  const [recent, setRecent] = useState(getRecent);

  const go = (h, remember = true) => {
    setPicked(h); setHits(null); onGo(h.lat, h.lon);
    if (remember && !h.coords) setRecent(addRecent({ name: h.name, lat: h.lat, lon: h.lon }));
  };
  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    const c = parseCoords(q);
    if (c) return go({ ...c, coords: true, name: `${c.lat.toFixed(6)}, ${c.lon.toFixed(6)}` });
    if (!online) return toast.error(t.addrOffline);
    setBusy(true);
    try {
      const list = await geoSearch(q.trim(), lang);
      if (!list.length) toast.error(t.addrNone);
      setHits(list);
    } catch { toast.error(t.addrErr); }
    setBusy(false);
  };

  return (
    <div className="addr" data-testid="address-search">
      <form className="addr-form" onSubmit={search}>
        <Search size={16} className="addr-ico" />
        <input type="search" data-testid="address-search-input" placeholder={t.addrPlaceholder} value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} enterKeyHint="search" />
        {q && <button type="button" className="icon-btn" data-testid="address-clear-btn" aria-label="clear" onClick={() => { setQ(''); setHits(null); setPicked(null); }}><X size={14} /></button>}
        <button type="submit" className="btn btn-dark btn-sm" data-testid="address-search-btn" disabled={busy}>{busy ? <Loader2 size={15} className="spin" /> : t.addrGo}</button>
      </form>
      {hits?.length > 0 && (
        <ul className="addr-hits" data-testid="address-results">
          {hits.map((h, i) => (
            <li key={`${h.lat}${h.lon}${i}`}><button data-testid={`address-result-${i}`} onClick={() => go(h)}><MapPin size={13} /> {h.name}</button></li>
          ))}
        </ul>
      )}
      {!hits?.length && !picked && recent.length > 0 && (
        <div className="addr-recent" data-testid="address-recent">
          <History size={13} />
          {recent.map((h, i) => <button key={h.name} className="addr-chip" data-testid={`address-recent-${i}`} title={h.name} onClick={() => { setQ(h.name); go(h, false); }}>{h.name.split(',').slice(0, 2).join(',')}</button>)}
          <button className="icon-btn" data-testid="address-recent-clear-btn" aria-label={t.addrClearRecent} title={t.addrClearRecent} onClick={() => { clearRecent(); setRecent([]); }}><Trash2 size={13} /></button>
        </div>
      )}
      {picked && (
        <div className="addr-picked" data-testid="address-picked">
          <span><MapPin size={13} /> {picked.name}</span>
          <div className="row">{children}</div>
        </div>
      )}
      {!kritisOn() && <small className="addr-attr">{t.addrAttr}</small>}
    </div>
  );
}
